import { Component, Inject } from '@angular/core';
import { UntypedFormControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { ChangeSetScheduleDialogData } from '@components/change-set-schedule-dialog/1.0';
import { ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { ThemePalette } from '@angular/material/core';
import { ChangeSetItem } from '@interfaces/change-sets/1.0';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { DateService } from '@services/utilities/1.0/date.service';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDateFormats,
  NgxMatDatetimePickerModule,
} from '@nng-components/angular-material-datetime-picker';
import { Subject } from 'rxjs';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf } from '@angular/common';
import { MatFormField, MatLabel, MatError, MatHint, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
import { ButtonComponent } from '../../button/1.0/button.component';
import { MessageComponent } from '../../message/1.0/message.component';
import { TranslateModule } from '@ngx-translate/core';

export const CUSTOM_DATE_FORMATS: NgxMatDateFormats = {
  parse: {
    dateInput: 'MM/dd/yy hh:mm a',
  },
  display: {
    dateInput: 'MM/dd/yy hh:mm a',
    monthYearLabel: 'MMM yyyy',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM yyyy',
  },
};

@Component({
  selector: 'txp-change-set-schedule-dialog',
  templateUrl: './change-set-schedule-dialog.component.html',
  styleUrls: ['./change-set-schedule-dialog.component.scss'],
  providers: [{ provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }],
  standalone: true,
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    NgIf,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatError,
    MatCheckbox,
    NgxMatDatetimePickerModule,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatIconButton,
    MatDialogActions,
    ButtonComponent,
    MessageComponent,
    TranslateModule,
  ],
})
export class ChangeSetScheduleDialogComponent {
  buttonStyle = ButtonStyle;
  disableContinueBtn: boolean;
  showError: boolean;
  messageType = MessageType;
  errorMessage: string;
  minDate: any;
  scheduleDateControl;
  versionNameControl: UntypedFormControl;
  scheduleTimeCheckboxControl: UntypedFormControl;
  changeSet: ChangeSetItem;
  hideScheduleTimeInput;

  public color: ThemePalette = 'primary';

  constructor(
    private dialogRef: MatDialogRef<ChangeSetScheduleDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ChangeSetScheduleDialogData,
    private dateService: DateService,
  ) {
    const now = new Date();
    const endOfDay = this.dateService.endOf(now, 'day');
    const endOfDayFormatted = this.dateService.formatLocalDateTime(endOfDay);

    this.hideScheduleTimeInput = !!this.data.input;
    this.changeSet = DeepCloneService.clone(this.data?.item || {});
    this.changeSet.scheduledTimestamp = endOfDayFormatted;

    const minDate = this.dateService.add(now, 1, 'hour');
    this.minDate = this.dateService.formatLocalDateTime(minDate);

    this.scheduleDateControl = new UntypedFormControl(
      data?.scheduledTimeStamp || this.changeSet?.scheduledTimestamp || endOfDayFormatted,
    );
    this.versionNameControl = new UntypedFormControl(data.versionName, Validators.required);
    this.scheduleTimeCheckboxControl = new UntypedFormControl(false);
  }

  onScheduleDateChange(event) {
    this.changeSet.scheduledTimestamp = this.dateService.formatLocalDateTime(event.value);
  }

  onCancel() {
    this.dialogRef.close(null);
  }

  onSave() {
    if (this.hasVersionConflict) {
      return;
    }
    if (this.data.input) {
      this.dialogRef.close({
        name: this.versionNameControl.value,
        scheduleTime:
          this.scheduleTimeCheckboxControl.value || this.data.scheduleOnly
            ? this.dateService.toUTCISOString(this.scheduleDateControl.value)
            : null,
      });
    } else {
      this.dialogRef.close(this.changeSet?.scheduledTimestamp);
    }
  }

  get isScheduleMode(): boolean {
    return !!(this.scheduleTimeCheckboxControl?.value || this.data?.scheduleOnly);
  }

  get hasVersionConflict(): boolean {
    if (this.isScheduleMode) {
      return !!this.data?.hasScheduledVersion;
    }
    return !!this.data?.hasDraftVersion;
  }

  get warningMessage(): string | null {
    if (this.isScheduleMode && this.data?.hasScheduledVersion) {
      return 'menu.information.scheduledMenuExistsWarning';
    }
    if (!this.isScheduleMode && this.data?.hasDraftVersion) {
      return 'menu.information.draftMenuExistsWarning';
    }
    return null;
  }

  get isSaveDisabled(): boolean {
    if (this.hasVersionConflict) {
      return true;
    }

    // If dialog is not in input mode and the date is invalid, always allow save
    if (!this.data?.input) {
      return this.scheduleDateControl?.invalid ?? false;
    }

    const scheduleRequested = this.scheduleTimeCheckboxControl?.value || this.data?.scheduleOnly;
    const dateInvalid = scheduleRequested ? this.scheduleDateControl?.invalid : false;
    return this.versionNameControl?.invalid || dateInvalid;
  }
}
